"use client";

import Link from "next/link";
import Image from "next/image";
import { signOut, useSession } from "next-auth/react";
import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import config from "@/config";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LogOut, History, LogIn, Activity, ShieldCheck } from "lucide-react";

export default function HeaderHome() {
  const { data: session } = useSession();
  const pathname = usePathname();
  const name = session?.user?.name ?? null;
  const email = session?.user?.email ?? null;
  const displayName = name || (email ? email.split("@")[0] : null) || "User";
  const imageUrl = session?.user?.image ?? null;
  const isCertifiedWebsitesPage = pathname?.startsWith("/certified-websites") ?? false;
  const [isLandingPage, setIsLandingPage] = useState(false);

  useEffect(() => {
    const pathname = window.location.pathname;
    setIsLandingPage(
      pathname === "/" || pathname.startsWith("/certified-websites/")
    );
  }, []);

  return (
    <header
      className={`${
        isLandingPage
          ? "relative border-b border-border/40"
          : "sticky top-0 left-0 right-0 z-50 border-b border-border/40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60"
      }`}
    >
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between relative">
          {/* Logo/Brand */}
          <Link
            href="/"
            aria-label="YourWebsiteScore home"
            className="flex items-center space-x-3 flex-shrink-0"
          >
            <Image
              src="/logo.svg"
              alt=""
              aria-hidden
              width={32}
              height={32}
              priority
              className="h-8 w-8"
            />
            <span className="hidden min-[800px]:inline font-bold text-xl text-foreground">
              YourWebsiteScore
            </span>
          </Link>

          {/* Center Navigation */}
          <nav className="hidden min-[430px]:flex items-center space-x-6 absolute left-1/2 -translate-x-1/2">
            {/* Landing Page Navigation - show for all users */}
            <button
              onClick={() => {
                // Check if we're on the landing page
                if (window.location.pathname === "/") {
                  const element = document.getElementById("features");
                  element?.scrollIntoView({ behavior: "smooth" });
                } else {
                  // Navigate to landing page with hash
                  window.location.href = "/#features";
                }
              }}
              className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
            >
              Features
            </button>
            <button
              onClick={() => {
                // Check if we're on the landing page
                if (window.location.pathname === "/") {
                  const element = document.getElementById("pricing");
                  element?.scrollIntoView({ behavior: "smooth" });
                } else {
                  // Navigate to landing page with hash
                  window.location.href = "/#pricing";
                }
              }}
              className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
            >
              Pricing
            </button>
            <button
              onClick={() => {
                // Check if we're on the landing page
                if (window.location.pathname === "/") {
                  const element = document.getElementById("faq");
                  element?.scrollIntoView({ behavior: "smooth" });
                } else {
                  // Navigate to landing page with hash
                  window.location.href = "/#faq";
                }
              }}
              className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
            >
              FAQ
            </button>

            {/* App Navigation - only show when logged in and not on certified websites pages */}
            {session && !isCertifiedWebsitesPage && (
              <>
                <Link
                  href={config.routes.analysis.path}
                  className="flex items-center space-x-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Activity className="h-4 w-4" />
                  <span className="text-center">Analysis</span>
                </Link>
                <Link
                  href={config.routes.history.path}
                  className="flex items-center space-x-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                >
                  <History className="h-4 w-4" />
                  <span className="text-center">History</span>
                </Link>
                <Link
                  href={config.routes.advanced.path}
                  className="flex items-center space-x-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                >
                  <ShieldCheck className="h-4 w-4" />
                  <span className="text-center">Advanced</span>
                </Link>
              </>
            )}
          </nav>

          {/* Login Button or User Dropdown */}
          <div className="flex-shrink-0">
            {session ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="relative h-8 w-8 sm:h-9 sm:w-auto sm:px-3 rounded-full sm:rounded-md"
                  >
                    <Avatar className="h-8 w-8 sm:h-6 sm:w-6 bg-muted">
                      <AvatarImage src={imageUrl ? "" : ""} alt="User" />
                      <AvatarFallback>
                        {displayName.slice(0, 1).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <span className="hidden sm:block text-sm font-medium text-muted-foreground">
                      {displayName}
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56" align="end">
                  <DropdownMenuLabel className="text-muted-foreground select-none">
                    {email || displayName}
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="cursor-pointer"
                    onClick={() => signOut({ callbackUrl: config.app.url })}
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>Sign Out</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              // Login button intentionally commented out per request
              <Button size="sm" asChild>
                <Link
                  href={config.routes.signin.path}
                  className="flex items-center gap-2"
                >
                  <LogIn className="h-4 w-4" />
                  Login
                </Link>
              </Button>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
